import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Search, Loader2, Layers, CalendarRange, Truck, Bell } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import OrderImpactAlert from './OrderImpactAlert';
import RecipientBreakdown from './RecipientBreakdown';

const WEEKDAY_OPTS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

/**
 * Create one menu and apply it across a DATE RANGE (optional weekday filter).
 * Two-step: build → preview conflicts (skip vs overwrite) → apply, optionally
 * publish + notify all clients (with a recipient-count confirm).
 */
const MenuRangeDialog = ({ open, onClose, products, onApplied }) => {
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState('additive');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekdays, setWeekdays] = useState([]); // [] = every day
  const [selectedIds, setSelectedIds] = useState([]);
  const [deliveryOverride, setDeliveryOverride] = useState(false);
  const [publish, setPublish] = useState(false);
  const [notify, setNotify] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [search, setSearch] = useState('');

  const [preview, setPreview] = useState(null); // {target_dates,new_dates,overwrite_dates}
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmNotify, setConfirmNotify] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactOpen, setImpactOpen] = useState(false);

  const activeProducts = useMemo(
    () => products.filter((p) => p.status === 'available' || p.status === 'active'),
    [products]
  );
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeProducts;
    return activeProducts.filter((p) => p.name.toLowerCase().includes(q));
  }, [activeProducts, search]);

  const toggleWeekday = (key) =>
    setWeekdays((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const toggleProduct = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const resetPreview = () => setPreview(null);

  const handlePreview = async () => {
    if (!startDate || !endDate) {
      toast.error('Pick a start and end date');
      return;
    }
    setPreviewing(true);
    try {
      const data = await adminService.previewMenuRange({
        start_date: startDate, end_date: endDate, weekday_mask: weekdays,
      });
      setPreview(data);
      if (data.target_dates.length === 0) {
        toast.warning('No dates in that range match the weekday filter');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const doApply = async () => {
    // Order-impact guard (fails open — never blocks the apply).
    setApplying(true);
    try {
      const proposed = {
        mode,
        published: publish,
        delivery_override: deliveryOverride,
        items: selectedIds.map((pid) => ({ product_id: pid })),
      };
      const applied = (preview?.target_dates || []).filter(
        (d) => !(preview?.overwrite_dates || []).includes(d) || overwrite
      );
      const result = await adminService.previewMenuOrderImpact({
        changes: applied.map((d) => ({ date: d, menu: proposed })),
      });
      if (result.total_orders_affected > 0) {
        setImpact(result);
        setImpactOpen(true);
        setApplying(false);
        setConfirmNotify(false);
        return;
      }
    } catch {
      // fail open
    }
    await reallyApply();
  };

  const reallyApply = async () => {
    setApplying(true);
    try {
      const result = await adminService.applyMenuRange({
        title: title.trim(),
        mode,
        start_date: startDate,
        end_date: endDate,
        weekday_mask: weekdays,
        items: selectedIds.map((pid) => ({ product_id: pid })),
        delivery_override: deliveryOverride,
        published: publish,
        overwrite,
        notify: publish && notify,
      });
      const parts = [`${result.created_count} created`];
      if (result.overwritten_count) parts.push(`${result.overwritten_count} overwritten`);
      if (result.skipped_count) parts.push(`${result.skipped_count} skipped`);
      if (result.notify_result) parts.push(`${result.notify_result.emails_sent} emails sent`);
      toast.success(`Menu applied — ${parts.join(', ')}`);
      onApplied();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply menu');
    } finally {
      setApplying(false);
      setConfirmNotify(false);
    }
  };

  const handleApply = async () => {
    if (!preview) {
      toast.error('Run a conflict preview first');
      return;
    }
    if (preview.target_dates.length === 0) {
      toast.error('No dates to apply');
      return;
    }
    if (publish && selectedIds.length === 0) {
      toast.error('Add at least one product before publishing');
      return;
    }
    if (publish && notify) {
      // Confirm with a recipient count before blasting clients.
      try {
        const { recipient_count } = await adminService.getMenuRecipientsCount();
        setRecipientCount(recipient_count);
      } catch {
        setRecipientCount(null);
      }
      setConfirmNotify(true);
      return;
    }
    doApply();
  };

  const overwriteCount = preview?.overwrite_dates?.length || 0;
  const targetCount = preview?.target_dates?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="menu-range-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-amber-600" /> Create Menu for a Date Range
          </DialogTitle>
          <DialogDescription>
            Apply one menu across many dates. It fans out to each matching day; the range stays
            editable as a single batch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Title */}
          <div>
            <Label htmlFor="range-title">Menu title</Label>
            <Input
              id="range-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Summer Special"
              className="mt-1"
              data-testid="range-title-input"
            />
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); resetPreview(); }}
                data-testid="range-start-date"
              />
            </div>
            <div>
              <Label>End date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); resetPreview(); }}
                data-testid="range-end-date"
              />
            </div>
          </div>

          {/* Weekday filter */}
          <div>
            <Label className="text-sm">Only these weekdays <span className="text-gray-400">(none = every day)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {WEEKDAY_OPTS.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => { toggleWeekday(w.key); resetPreview(); }}
                  data-testid={`range-weekday-${w.key}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    weekdays.includes(w.key)
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mode */}
          <div>
            <Label className="flex items-center gap-2"><Layers className="h-4 w-4 text-amber-600" /> Menu mode</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${mode === 'additive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="additive" data-testid="range-mode-additive" className="mt-1" />
                <div>
                  <p className="text-sm font-medium">Additive</p>
                  <p className="text-xs text-gray-500">Adds items on top of the normal catalog.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${mode === 'exclusive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="exclusive" data-testid="range-mode-exclusive" className="mt-1" />
                <div>
                  <p className="text-sm font-medium">Exclusive</p>
                  <p className="text-xs text-gray-500">Only these items are available that day.</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Products */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Products</Label>
              <span className="text-xs text-gray-500" data-testid="range-selected-count">{selectedIds.length} selected</span>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="range-product-search"
              />
            </div>
            <ScrollArea className="h-44 rounded-lg border">
              <div className="p-2 space-y-1">
                {filteredProducts.length === 0 ? (
                  <p className="text-sm text-gray-400 p-3">No products found</p>
                ) : (
                  filteredProducts.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer" data-testid={`range-product-row-${p.id}`}>
                      <Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} data-testid={`range-product-check-${p.id}`} />
                      {p.image_url && <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-gray-400 uppercase">{p.category}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Delivery override */}
          <div className="flex items-start justify-between rounded-lg border border-dashed p-3">
            <div className="flex items-start gap-2 pr-3">
              <Truck className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Deliver to everyone on these days</p>
                <p className="text-xs text-gray-500">Make these dates orderable for all clients, regardless of their normal schedule.</p>
              </div>
            </div>
            <Switch checked={deliveryOverride} onCheckedChange={setDeliveryOverride} data-testid="range-override-switch" />
          </div>

          {/* Preview */}
          <div className="rounded-lg border p-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Conflict preview</p>
              <Button variant="outline" size="sm" onClick={handlePreview} disabled={previewing} data-testid="range-preview-btn">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview dates'}
              </Button>
            </div>
            {preview && (
              <div className="mt-3 text-sm space-y-2" data-testid="range-preview-result">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-blue-100 text-blue-800">{targetCount} matching dates</Badge>
                  <Badge className="bg-green-100 text-green-800">{preview.new_dates.length} new</Badge>
                  {overwriteCount > 0 && (
                    <Badge className="bg-orange-100 text-orange-800" data-testid="range-overwrite-count">{overwriteCount} already have a menu</Badge>
                  )}
                </div>
                {overwriteCount > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer pt-1">
                    <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="range-overwrite-check" />
                    <span className="text-xs text-gray-700">Overwrite existing menus on those {overwriteCount} date(s) (otherwise they're skipped)</span>
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Publish + notify */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Publish immediately</p>
              <p className="text-xs text-gray-500">Published menus are visible to clients for ordering.</p>
            </div>
            <Switch checked={publish} onCheckedChange={(v) => { setPublish(v); if (!v) setNotify(false); }} data-testid="range-publish-switch" />
          </div>
          {publish && (
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-amber-200 bg-amber-50 p-3">
              <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="range-notify-check" />
              <span className="flex items-center gap-1.5 text-sm text-amber-800">
                <Bell className="h-4 w-4" /> Notify all clients this menu is live (email with product images + their pricing)
              </span>
            </label>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={handleApply}
            disabled={applying || !preview}
            data-testid="range-apply-btn"
          >
            {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Applying…</> : 'Apply Menu'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmNotify} onOpenChange={setConfirmNotify}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notify clients?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email{' '}
              <RecipientBreakdown count={recipientCount} testId="range-recipient-count" />{' '}
              that &ldquo;{title || 'this menu'}&rdquo; is live. Clients, their team and their
              extra contacts get product images and that account&apos;s pricing; marketing
              recipients get images and descriptions with no pricing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={doApply} data-testid="range-confirm-notify-btn">
              Apply &amp; Notify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <OrderImpactAlert
        open={impactOpen}
        onOpenChange={setImpactOpen}
        impact={impact}
        confirmLabel="Apply anyway"
        onProceed={() => { setImpactOpen(false); reallyApply(); }}
      />
    </Dialog>
  );
};

export default MenuRangeDialog;
